import React from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  Activity,
  CheckCircle, 
  XCircle, 
  Clock, 
  Loader2, 
  Upload,
  AlertTriangle,
  FileText,
  Image,
  Trash2,
  RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useOperations } from '../../contexts/OperationsContext';
import { 
  AnimatedFileItem, 
  AnimatedProgressSegment, 
  AnimatedStatusBadge,
  staggerContainer,
  fadeInUp 
} from '../animations/AnimatedOperations';

const SegmentedProgressBar = ({ files, totalFiles }) => {
  if (totalFiles === 0) return null;

  return (
    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden flex">
      <AnimatePresence>
        {files.map((file, index) => {
          let colorClass = 'bg-slate-700'; // default/pending
          
          switch (file.status) {
            case 'completed':
              colorClass = 'bg-green-500';
              break;
            case 'failed':
              colorClass = 'bg-red-500';
              break;
            case 'processing':
              colorClass = 'bg-blue-500';
              break;
            case 'uploading':
              colorClass = 'bg-yellow-500';
              break;
            case 'cancelled':
              colorClass = 'bg-gray-500';
              break;
            default:
              colorClass = 'bg-slate-600';
          }

          return (
            <AnimatedProgressSegment
              key={file.tempId || index}
              delay={index * 0.02}
              className={cn(
                'transition-all duration-500 ease-in-out',
                colorClass,
                file.status === 'processing' && 'animate-pulse'
              )}
              style={{ 
                width: `${100 / totalFiles}%`,
                minWidth: '2px'
              }}
              title={`${file.fileName}: ${file.status}`}
            />
          );
        })}
      </AnimatePresence>
    </div>
  );
};

const FileStatusIcon = ({ status, size = 'h-3 w-3' }) => {
  switch (status) {
    case 'uploading':
      return <Upload className={cn(size, 'text-yellow-500 animate-pulse')} />;
    case 'pending':
      return <Clock className={cn(size, 'text-slate-400')} />;
    case 'processing':
      return <Loader2 className={cn(size, 'text-blue-500 animate-spin')} />;
    case 'completed':
      return <CheckCircle className={cn(size, 'text-green-500')} />;
    case 'failed':
      return <XCircle className={cn(size, 'text-red-500')} />;
    case 'cancelled':
      return <XCircle className={cn(size, 'text-gray-500')} />;
    default:
      return <FileText className={cn(size, 'text-slate-500')} />;
  }
};

const FileTypeIcon = ({ file, size = 'h-3 w-3' }) => {
  const isPdf = file.fileType === 'pdf' || file.file?.type === 'application/pdf';
  return isPdf ? 
    <FileText className={cn(size, 'text-red-500')} /> : 
    <Image className={cn(size, 'text-green-500')} />;
};

const OperationsSummary = () => {
  const {
    totalFiles,
    completedFiles,
    failedFiles,
    processingFiles,
    pendingFiles,
    uploadingFiles,
    hasActiveOperations,
    hasFailedOperations,
    isEmpty
  } = useOperations();

  if (isEmpty) return null;

  const getStatusText = () => {
    if (hasFailedOperations && hasActiveOperations) {
      return `${failedFiles} failed, ${processingFiles + pendingFiles + uploadingFiles} active`;
    } else if (hasFailedOperations) {
      return `${failedFiles} failed`;
    } else if (hasActiveOperations) {
      const activeCount = processingFiles + pendingFiles + uploadingFiles;
      return `${activeCount} processing`;
    } else {
      return `${completedFiles} completed`;
    }
  };

  const getStatusColor = () => {
    if (hasFailedOperations) return 'text-red-400';
    if (hasActiveOperations) return 'text-blue-400';
    return 'text-green-400';
  };

  const getStatusIcon = () => {
    if (hasFailedOperations) return <AlertTriangle className="h-3 w-3 text-red-500" />;
    if (hasActiveOperations) return <Activity className="h-3 w-3 text-blue-500" />;
    return <CheckCircle className="h-3 w-3 text-green-500" />;
  };

  return (
    <div className="text-xs space-y-1">
      <div className="flex items-center justify-between">
        <span className={getStatusColor()}>{getStatusText()}</span>
        <span className="text-slate-500">{totalFiles} total</span>
      </div>
      <div className="flex items-center gap-1">
        {getStatusIcon()}
        <span className={cn('text-xs', getStatusColor())}>
          {completedFiles}/{totalFiles}
        </span>
      </div>
    </div>
  );
};

const FileOperationItem = ({ file, index }) => {
  const {
    handleCancelFile,
    handleRetryFile,
    handleRemoveFile
  } = useOperations();

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
  };

  const canCancel = file.status === 'uploading' || file.status === 'pending';
  const canRetry = file.status === 'failed';
  const canRemove = file.status !== 'processing';

  return (
    <AnimatedFileItem 
      index={index}
      className="group flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#151415] transition-colors"
    >
      <FileTypeIcon file={file} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-200 truncate font-medium">
            {file.fileName}
          </span>
          <AnimatedStatusBadge status={file.status}>
            <FileStatusIcon status={file.status} />
          </AnimatedStatusBadge>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>{formatFileSize(file.fileSize)}</span>
          {file.error && (
            <motion.span 
              className="text-red-400 truncate" 
              title={file.error}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {file.error.substring(0, 30)}...
            </motion.span>
          )}
        </div>
      </div>

      {/* Actions - show on hover */}
      <motion.div 
        className="flex items-center gap-1 opacity-0 group-hover:opacity-100"
        initial={false}
        animate={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {canRetry && (
          <motion.button
            onClick={() => handleRetryFile(file.tempId)}
            className="p-1 hover:bg-[#494646] rounded text-blue-400 hover:text-blue-300"
            title="Retry"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <RotateCcw className="h-3 w-3" />
          </motion.button>
        )}
        {canRemove && (
          <motion.button
            onClick={() => handleRemoveFile(file.tempId)}
            className="p-1 hover:bg-[#494646] rounded text-slate-400 hover:text-slate-200"
            title="Remove"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <XCircle className="h-3 w-3" />
          </motion.button>
        )}
        {canCancel && (
          <motion.button
            onClick={() => handleCancelFile(file.tempId)}
            className="p-1 hover:bg-[#494646] rounded text-red-400 hover:text-red-300"
            title="Cancel"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <XCircle className="h-3 w-3" />
          </motion.button>
        )}
      </motion.div>
    </AnimatedFileItem>
  );
};

export const OperationsSection = () => {
  const {
    isEmpty,
    totalFiles,
    sortedFiles,
    hasFailedOperations,
    hasActiveOperations,
    isOperationsExpanded,
    toggleOperationsExpanded,
    handleClearCompleted
  } = useOperations();

  return (
    <div className="relative">
      {/* Expanded overlay that extends upward */}
      <AnimatePresence>
        {isOperationsExpanded && !isEmpty && (
          <motion.div 
            className="absolute bottom-full left-0 right-0 bg-[#2a2829] border-t border-[#494646] shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            style={{ maxHeight: '50vh' }}
          >
            <div className="p-4 overflow-y-auto" style={{ maxHeight: '50vh' }}>
              {/* Clear completed button */}
              {sortedFiles.some(f => f.status === 'completed' || f.status === 'failed' || f.status === 'cancelled') && (
                <motion.div 
                  className="flex justify-end mb-2"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <motion.button
                    onClick={handleClearCompleted}
                    className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Trash2 className="h-3 w-3" />
                    Clear completed
                  </motion.button>
                </motion.div>
              )}

              {/* Files list */}
              <motion.div 
                className="space-y-0.5"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                <AnimatePresence>
                  {sortedFiles.map((file, index) => (
                    <FileOperationItem key={file.tempId} file={file} index={index} />
                  ))}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main operations section */}
      <div className="px-4 py-4 bg-[#2a2829]">
      {/* Operations Header - matches Projects styling */}
      <div className="flex items-center gap-2 mb-3">
        <Activity className={cn(
          "h-4 w-4",
          isEmpty ? "text-slate-500" : hasFailedOperations ? "text-red-400" : hasActiveOperations ? "text-blue-400" : "text-slate-400"
        )} />
        <span className={cn(
          "text-sm font-medium",
          isEmpty ? "text-slate-400" : "text-slate-200"
        )}>Operations</span>
        
        {/* Status indicators */}
        <div className="ml-auto flex items-center gap-1">
          {hasFailedOperations && (
            <div className="w-2 h-2 bg-red-500 rounded-full" />
          )}
          {hasActiveOperations && !hasFailedOperations && (
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          )}
          {!isEmpty && (
            <span className="text-xs text-slate-400">{totalFiles}</span>
          )}
        </div>
      </div>

      {/* Operations Toggle Button - only show if there are operations */}
      {!isEmpty && (
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleOperationsExpanded}
          className={cn(
            "w-full justify-start text-xs h-7 px-2 mb-2",
            "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          )}
        >
          {isOperationsExpanded ? (
            <ChevronDown className="h-3 w-3 mr-2" />
          ) : (
            <ChevronRight className="h-3 w-3 mr-2" />
          )}
          {isOperationsExpanded ? 'Hide Details' : 'Show Details'}
        </Button>
      )}

      {/* Progress Bar and Summary - only show when there are operations */}
      {!isEmpty && (
        <div className="mb-3">
          <SegmentedProgressBar files={sortedFiles} totalFiles={totalFiles} />
          <div className="mt-1">
            <OperationsSummary />
          </div>
        </div>
      )}
      
      {/* Empty state message */}
      {isEmpty && (
        <div className="text-xs text-slate-500 italic">
          No file operations this session
        </div>
      )}
      </div>
    </div>
  );
};